import { useContext, useEffect, useMemo } from 'react';
import { fetchDeviceKVStore, fetchHomeKVStore, selectDeviceKVStore, selectHomeKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { fixSensorModuleData, getSensorModuleKVStoreKey, SensorCloudKeyValuePair, SensorModule } from './SCUtils';


// This file contains custom hooks for selecting/fetching data from data state. On each page or container, we usually select some
// data we need from the data state. And if the data is not fetched, we dispatch an action to fetch the data and when the data is ready,
// we make try to select the data again. We do this a lot therefore, we will create custom hooks to handle these select/fetch data so
// that we can do everything with just 1 line



/**
 * A custom hook to select a HOME KV store, used for storing sensor module, info from the data state and fetch device data if data is not loaded.
 * @param homeId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectHomeSensorModuleKVStore = (
    homeId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorCloudKeyValuePair | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);

    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        if (homeId && moduleId) {
            const key = getSensorModuleKVStoreKey(moduleId);
            const kvPair = selectHomeKVStore(state, homeId, key);

            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SensorCloudKeyValuePair;
        }
        return undefined;
    }, [homeId, moduleId, state]);


    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (homeId && moduleId && fetchLatest) {
            const key = getSensorModuleKVStoreKey(moduleId);
            dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                // ignore error because dispatch already handle showing the error
            });
        }
    }, [dispatch, fetchLatest, homeId, moduleId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (homeId && moduleId && !sensorModuleKVStore) {
            const key = getSensorModuleKVStoreKey(moduleId);
            dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                // ignore error because dispatch already handle showing the error
            });
        }
    }, [dispatch, homeId, moduleId, sensorModuleKVStore, showLoading, suppressErrors]);

    return sensorModuleKVStore;
};



/**
 * A custom hook to select a DEVICE KV store, used for storing sensor module, info from the data state and fetch device data if data is not loaded.
 * @param deviceId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectDeviceSensorModuleKVStore = (
    deviceId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorCloudKeyValuePair | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);

    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        if (deviceId && moduleId) {
            const key = getSensorModuleKVStoreKey(moduleId);
            const kvPair = selectDeviceKVStore(state, deviceId, key);

            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SensorCloudKeyValuePair;
        }
        return undefined;
    }, [deviceId, moduleId, state]);


    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (deviceId && moduleId && fetchLatest) {
            const key = getSensorModuleKVStoreKey(moduleId);
            dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                // ignore error because dispatch already handle showing the error
            });
        }
    }, [dispatch, fetchLatest, deviceId, moduleId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (deviceId && moduleId && !sensorModuleKVStore) {
            const key = getSensorModuleKVStoreKey(moduleId);
            dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                // ignore error because dispatch already handle showing the error
            });
        }
    }, [dispatch, deviceId, moduleId, sensorModuleKVStore, showLoading, suppressErrors]);

    return sensorModuleKVStore;
};


/**
 * A custom hook to select a DEVICE or HOME KV store, used for storing sensor module, info from the data state and fetch device data if data
 * is not loaded.
 * @param deviceId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectSensorModuleKVStore = (
    homeId: number | undefined, deviceId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorCloudKeyValuePair | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);

    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        if ((deviceId || homeId) && moduleId) {
            const key = getSensorModuleKVStoreKey(moduleId);
            const homeKV = homeId ? selectHomeKVStore(state, homeId, key) : undefined;
            const deviceKV = deviceId ? selectDeviceKVStore(state, deviceId, key) : undefined;
            const kvPair = homeKV || deviceKV;

            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SensorCloudKeyValuePair;
        }
        return undefined;
    }, [deviceId, homeId, moduleId, state]);


    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if ((deviceId || homeId) && moduleId && fetchLatest) {
            const key = getSensorModuleKVStoreKey(moduleId);
            if (deviceId) {
                dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                    // if we failed to fetch device KV, try home KV if homeId is provided
                    if (homeId) {
                        dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                            // ignore error because dispatch already handle showing the error
                        });
                    }
                });
            } else if (homeId) {
                dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                    // if we failed to fetch home KV, try device KV if deviceId is provided
                    if (deviceId) {
                        dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                            // ignore error because dispatch already handle showing the error
                        });
                    }
                });
            }
        }
    }, [dispatch, fetchLatest, deviceId, moduleId, homeId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if ((deviceId || homeId) && moduleId && !sensorModuleKVStore) {
            const key = getSensorModuleKVStoreKey(moduleId);
            if (deviceId) {
                dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                    // if we failed to fetch device KV, try home KV if homeId is provided
                    if (homeId) {
                        dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                            // ignore error because dispatch already handle showing the error
                        });
                    }
                });
            } else if (homeId) {
                dispatch(fetchHomeKVStore(homeId, key), showLoading, suppressErrors).catch(() => {
                    // if we failed to fetch home KV, try device KV if deviceId is provided
                    if (deviceId) {
                        dispatch(fetchDeviceKVStore(deviceId, key), showLoading, suppressErrors).catch(() => {
                            // ignore error because dispatch already handle showing the error
                        });
                    }
                });
            }
        }
    }, [dispatch, deviceId, moduleId, sensorModuleKVStore, homeId, showLoading, suppressErrors]);

    return sensorModuleKVStore;
};



/**
 * A custom hook to select a HOME sensor module object
 * @param homeId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectHomeSensorModule = (
    homeId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorModule | undefined => {

    const sensorModuleKVStore = useSelectHomeSensorModuleKVStore(homeId, moduleId, fetchLatest, showLoading, suppressErrors);
    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);

    return sensorModule;
};



/**
 * A custom hook to select a DEVICE sensor module object
 * @param deviceId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectDeviceSensorModule = (
    deviceId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorModule | undefined => {

    const sensorModuleKVStore = useSelectDeviceSensorModuleKVStore(deviceId, moduleId, fetchLatest, showLoading, suppressErrors);
    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);

    return sensorModule;
};



/**
 * A custom hook to select a DEVICE sensor module by ID from the data state and fetch device data if data is not loaded.
 * @param deviceId
 * @param moduleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectSensorModule = (
    homeId: number | undefined, deviceId: number | undefined, moduleId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): SensorModule | undefined => {
    const sensorModuleKVStore = useSelectSensorModuleKVStore(homeId, deviceId, moduleId, fetchLatest, showLoading, suppressErrors);
    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);

    return sensorModule;
};
